import * as THREE from "three";

import { LANDMARK } from "./landmarks";

/**
 * The three ways one face differs from another that change where a pair of
 * glasses has to sit. `computeFacePose` already gives us position, orientation
 * and overall size; these describe *shape*, which is what's left over once
 * scale is divided out.
 *
 * Every value is dimensionless, so none of them move when the wearer leans
 * toward the camera, the window is resized, or the webcam swaps resolution.
 *
 *   eyeSpanRatio      Eye-centre separation over face width. Two people with
 *                     identically wide faces can want frames a size apart if
 *                     one has wide-set eyes.
 *   eyeDropRatio      How far the eye line sits *below* the bridge anchor, in
 *                     face widths. Decides whether the lenses land on the eyes
 *                     or up on the brow.
 *   bridgeProtrusion  Nasion protrusion as a fraction of the nose tip's — a
 *                     flat bridge with a prominent tip reads low, a strong
 *                     straight nose reads high. Deliberately a ratio of one
 *                     depth to another: MediaPipe's z is only approximately to
 *                     scale, and stating this as a fraction of a depth measured
 *                     on the same face cancels that error out.
 */

// Reused across frames; this runs once per rendered frame, same as the pose.
const pBridge = new THREE.Vector3();
const pNoseTip = new THREE.Vector3();
const pCheekMid = new THREE.Vector3();
const pLeftEye = new THREE.Vector3();
const pRightEye = new THREE.Vector3();
const pEyeMid = new THREE.Vector3();
const pScratch = new THREE.Vector3();
const axisUp = new THREE.Vector3();
const axisForward = new THREE.Vector3();

/** Highest index we read, so a truncated landmark array can't produce NaNs. */
const REQUIRED_LANDMARKS = LANDMARK.FACE_RIGHT + 1;

/**
 * Measures the proportions of the face the pose was just built from.
 *
 * Takes the head's own axes from the pose quaternion rather than rebuilding
 * them, which keeps the two in lockstep: every offset derived from these
 * metrics is applied along exactly the axes they were measured against.
 */
export function computeFaceMetrics(landmarks, mapping, pose, target) {
  if (landmarks.length < REQUIRED_LANDMARKS || pose.faceWidth < 1e-3) return null;

  axisUp.set(0, 1, 0).applyQuaternion(pose.quaternion);
  axisForward.set(0, 0, 1).applyQuaternion(pose.quaternion);

  mapping.toWorld(landmarks[LANDMARK.NOSE_BRIDGE], pBridge);
  mapping.toWorld(landmarks[LANDMARK.NOSE_TIP], pNoseTip);

  mapping.toWorld(landmarks[LANDMARK.FACE_LEFT], pCheekMid);
  mapping.toWorld(landmarks[LANDMARK.FACE_RIGHT], pScratch);
  pCheekMid.add(pScratch).multiplyScalar(0.5);

  // An eye's centre is the midpoint of its corners. The irises (468-477) would
  // be more precise, but they are absent from the canonical face model the
  // reference proportions come from, and a metric is only meaningful compared
  // against a reference measured the identical way.
  eyeCentre(landmarks, mapping, LANDMARK.LEFT_EYE_INNER, LANDMARK.LEFT_EYE_OUTER, pLeftEye);
  eyeCentre(landmarks, mapping, LANDMARK.RIGHT_EYE_INNER, LANDMARK.RIGHT_EYE_OUTER, pRightEye);
  pEyeMid.addVectors(pLeftEye, pRightEye).multiplyScalar(0.5);

  // Depths are measured from the line between the cheek landmarks, which sits
  // well behind the face and moves with it.
  const nasionDepth = pScratch.subVectors(pBridge, pCheekMid).dot(axisForward);
  const noseTipDepth = pScratch.subVectors(pNoseTip, pCheekMid).dot(axisForward);

  target.eyeSpanRatio = pLeftEye.distanceTo(pRightEye) / pose.faceWidth;
  target.eyeDropRatio = pScratch.subVectors(pBridge, pEyeMid).dot(axisUp) / pose.faceWidth;
  // A face turned far enough that the nose tip falls behind the cheek line is
  // past the point where any of this is measurable.
  target.bridgeProtrusion = noseTipDepth > 1e-3 ? nasionDepth / noseTipDepth : 0;

  if (
    !Number.isFinite(target.eyeSpanRatio) ||
    !Number.isFinite(target.eyeDropRatio) ||
    !Number.isFinite(target.bridgeProtrusion)
  ) {
    return null;
  }

  return target;
}

export function createEmptyMetrics() {
  return { eyeSpanRatio: 0, eyeDropRatio: 0, bridgeProtrusion: 0 };
}

function eyeCentre(landmarks, mapping, inner, outer, target) {
  mapping.toWorld(landmarks[inner], target);
  mapping.toWorld(landmarks[outer], pScratch);
  return target.add(pScratch).multiplyScalar(0.5);
}
